/**
 * Canvas AI Tool Registry — batch1 (~78) + batch2 (~45)
 * Excluded: ecological, matrix, interaction package
 */

export const EXCLUDED_TOOL_PREFIXES = ['ecological.', 'matrix.', 'interaction.'];

/** 跨页变更：不给模型和执行器 */
export const FORBIDDEN_MUTATION_TOOLS = [
  'page.switch',
  'page.create_main',
  'page.create_child',
  'page.copy',
  'page.delete',
  'engine.ensure_pages_loaded',
  'project.set_size',
];

/** 保存只走工具栏 saveProject，AI 禁止另写一套落库 */
export const FORBIDDEN_SAVE_TOOLS = [
  'project.save',
  'engine.flush_page',
  'engine.persist_page',
];

export const TOOL_REGISTRY = [
  // --- Context (8) ---
  { name: 'context.get_current_page', batch: 1, description: '获取当前页 id、名称、组件数' },
  { name: 'context.get_project_limits', batch: 1, description: '获取页数/按钮数等套餐上限' },
  { name: 'context.list_components', batch: 1, description: '列出当前页所有组件摘要' },
  { name: 'context.get_component', batch: 1, description: '按 id 或 alias 获取组件', params: { ref: 'string' } },
  { name: 'context.list_pages', batch: 1, description: '获取页面树' },
  { name: 'context.query_buttons', batch: 1, description: '筛选所有按钮' },
  { name: 'context.get_selection', batch: 1, description: '获取当前选中组件' },
  { name: 'context.check_quota', batch: 1, description: '检查是否可再添加 N 个按钮', params: { count: 'number' } },

  // --- Page + Back (12) ---
  { name: 'page.switch', batch: 1, description: '切换当前页', params: { pageId: 'string', parentId: 'string?' } },
  { name: 'page.create_main', batch: 1, description: '新建主页面' },
  { name: 'page.create_child', batch: 1, description: '在当前主页面下新建子页面' },
  { name: 'page.copy', batch: 1, description: '复制当前页' },
  { name: 'page.delete', batch: 1, description: '删除指定页', params: { pageId: 'string' } },
  { name: 'back.set_name', batch: 1, description: '设置页面名称', params: { name: 'string' } },
  { name: 'back.set_background_color', batch: 1, description: '设置背景色', params: { color: 'string' } },
  { name: 'back.set_background_image', batch: 1, description: '设置背景图', params: { fileId: 'string', filePath: 'string' } },
  { name: 'back.set_grid', batch: 1, description: '开关网格', params: { enabled: 'boolean' } },
  { name: 'back.set_page_size', batch: 1, description: '设置页宽高', params: { width: 'number', height: 'number' } },
  { name: 'back.set_menu_visible', batch: 1, description: '是否在菜单展示', params: { visible: 'boolean' } },
  { name: 'back.set_opacity', batch: 1, description: '背景透明度', params: { opacity: 'number' } },

  // --- Components (22) ---
  { name: 'component.add', batch: 1, description: '添加组件', params: { type: 'string', x: 'number', y: 'number', text: 'string?', alias: 'string?' } },
  { name: 'component.add_text', batch: 1, params: { x: 'number', y: 'number', text: 'string?' } },
  { name: 'component.add_line', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_button_square', batch: 1, params: { x: 'number', y: 'number', text: 'string?', alias: 'string?' } },
  { name: 'component.add_button_round', batch: 1, params: { x: 'number', y: 'number', text: 'string?', alias: 'string?' } },
  { name: 'component.add_button_round_angle', batch: 1, params: { x: 'number', y: 'number', text: 'string?', alias: 'string?' } },
  { name: 'component.add_picture', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_stream_media', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_video', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_music', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_weather', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_carousel', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_date', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_environment', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_document', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_webpage', batch: 1, params: { x: 'number', y: 'number', url: 'string?' } },
  { name: 'component.add_slider', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_status_light', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_child_page', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.add_visualization', batch: 1, params: { x: 'number', y: 'number' } },
  { name: 'component.delete', batch: 1, params: { ref: 'string?' } },
  { name: 'component.delete_batch', batch: 1, params: { refs: 'string[]' } },
  { name: 'component.select', batch: 1, params: { ref: 'string' } },
  { name: 'component.select_by_alias', batch: 1, params: { alias: 'string' } },
  { name: 'component.clear_canvas', batch: 1, description: '清空当前页组件（保留 Back）' },
  { name: 'component.copy', batch: 1 },
  { name: 'component.paste', batch: 1, params: { isMouse: 'boolean?' } },
  { name: 'component.duplicate', batch: 1, params: { ref: 'string?', offsetX: 'number?', offsetY: 'number?' } },

  // --- Layout (11) ---
  { name: 'layout.move', batch: 1, params: { ref: 'string?', left: 'number?', top: 'number?' } },
  { name: 'layout.resize', batch: 1, params: { ref: 'string?', width: 'number?', height: 'number?' } },
  { name: 'layout.set_position_box', batch: 1, params: { ref: 'string?', left: 'number', top: 'number', width: 'number', height: 'number' } },
  { name: 'layout.align_left', batch: 1, params: { refs: 'string[]?' } },
  { name: 'layout.align_right', batch: 1, params: { refs: 'string[]?' } },
  { name: 'layout.align_top', batch: 1, params: { refs: 'string[]?' } },
  { name: 'layout.align_bottom', batch: 1, params: { refs: 'string[]?' } },
  { name: 'layout.align_vertical_center', batch: 1, params: { refs: 'string[]?' } },
  { name: 'layout.distribute_horizontal', batch: 1, params: { refs: 'string[]?' } },
  { name: 'layout.distribute_vertical', batch: 1, params: { refs: 'string[]?' } },
  { name: 'layout.grid_place', batch: 1, params: { refs: 'string[]?', columns: 'number', gapX: 'number?', gapY: 'number?', startX: 'number?', startY: 'number?' } },

  // --- Style (10) ---
  { name: 'style.set_attr', batch: 1, params: { ref: 'string', path: 'string', value: 'any' } },
  { name: 'style.set_position_common', batch: 1, params: { ref: 'string?', top: 'number?', left: 'number?', width: 'number?', height: 'number?', rotate: 'number?', opacity: 'number?' } },
  { name: 'style.set_button_text', batch: 1, params: { ref: 'string', text: 'string' } },
  { name: 'style.set_button_colors', batch: 1, params: { ref: 'string', color: 'string?', backgroundColor: 'string?' } },
  { name: 'style.set_button_press_colors', batch: 1, params: { ref: 'string', pressBackgroundColor: 'string?' } },
  { name: 'style.set_button_background_type', batch: 1, params: { ref: 'string', backgroundType: 'string', pressBackgroundType: 'string?' } },
  { name: 'style.set_button_font', batch: 1, params: { ref: 'string', fontSize: 'number?', fontFamily: 'string?' } },
  { name: 'style.set_button_alias', batch: 1, params: { ref: 'string', alias: 'string' } },
  { name: 'style.set_button_one_click', batch: 1, params: { ref: 'string', oneClick: 'boolean' } },
  { name: 'style.batch_set', batch: 1, params: { updates: 'object' } },

  // --- Network (6) ---
  { name: 'network.set_ip_port', batch: 1, params: { ref: 'string?', ip: 'string', port: 'string' } },
  { name: 'network.bind_directives', batch: 1, params: { ref: 'string', directives: 'array' } },
  { name: 'network.bind_directives2', batch: 1, params: { ref: 'string', directives: 'array' } },
  { name: 'network.search_directives', batch: 1, params: { keyword: 'string', pageNum: 'number?', pageSize: 'number?' } },
  { name: 'network.bind_by_instruction_names', batch: 1, params: { ref: 'string', names: 'string[]' } },
  { name: 'network.bind_grid_buttons', batch: 1, params: { buttonRefs: 'string[]', instructionNames: 'string[]' } },

  // --- Events (8) ---
  { name: 'event.set_mutually', batch: 1, params: { ref: 'string', targetIds: 'string[]' } },
  { name: 'event.set_jump', batch: 1, params: { ref: 'string', pageIds: 'string[]' } },
  { name: 'event.set_press', batch: 1, params: { ref: 'string', targetIds: 'string[]' } },
  { name: 'event.set_press_again', batch: 1, params: { ref: 'string', targetIds: 'string[]' } },
  { name: 'event.set_click_bind', batch: 1, params: { ref: 'string', targetRef: 'string', eventName: 'string' } },
  { name: 'event.clear', batch: 1, params: { ref: 'string', eventType: 'string' } },
  { name: 'event.bind_mutually_group', batch: 1, params: { refs: 'string[]' } },
  { name: 'event.video_instruction', batch: 1, params: { ref: 'string', time: 'number', instructionNames: 'string[]' } },

  // --- Links (7) ---
  { name: 'link.add', batch: 1, params: { ref: 'string', targetRef: 'string', listenEvent: 'string', changeAttrs: 'array' } },
  { name: 'link.update', batch: 1, params: { ref: 'string', linkIndex: 'number', patch: 'object' } },
  { name: 'link.remove', batch: 1, params: { ref: 'string', linkIndex: 'number' } },
  { name: 'link.set_transition_time', batch: 1, params: { ref: 'string', ms: 'number' } },
  { name: 'link.add_change_attr', batch: 1, params: { ref: 'string', linkIndex: 'number', key: 'string', value: 'any' } },
  { name: 'link.remove_change_attr', batch: 1, params: { ref: 'string', linkIndex: 'number', attrIndex: 'number' } },
  { name: 'link.bind_on_event', batch: 1, params: { ref: 'string', targetRef: 'string', listenEvent: 'string', changeAttrs: 'array', transitionTime: 'number?' } },

  // --- Plan / History / Project (8) ---
  { name: 'plan.preview', batch: 1, description: '标记进入预览（由执行器处理）' },
  { name: 'plan.apply', batch: 1, description: '确认应用预览' },
  { name: 'plan.discard', batch: 1, description: '丢弃预览' },
  { name: 'history.undo', batch: 1 },
  { name: 'history.redo', batch: 1 },
  { name: 'history.snapshot', batch: 1 },
  { name: 'project.set_size', batch: 1, params: { width: 'number', height: 'number' } },
  { name: 'project.save', batch: 1 },
  { name: 'project.preview', batch: 1 },

  // --- Batch2: Attr by type (~16) ---
  { name: 'attr.v_text.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.picture.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.line.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.stream_media.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.video.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.music.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.weather.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.carousel.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.date.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.environment.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.document.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.webpage.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.slider.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.status_light.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.status_light.monitor', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.child_page.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.visualization.set', batch: 2, params: { ref: 'string', patch: 'object' } },
  { name: 'attr.back.set', batch: 2, params: { patch: 'object' } },

  // --- Batch2: Compose / Layer (10) ---
  { name: 'compose.group', batch: 2 },
  { name: 'compose.ungroup', batch: 2 },
  { name: 'layer.bring_forward', batch: 2, params: { ref: 'string?' } },
  { name: 'layer.send_backward', batch: 2, params: { ref: 'string?' } },
  { name: 'layer.bring_to_front', batch: 2, params: { ref: 'string?' } },
  { name: 'layer.send_to_back', batch: 2, params: { ref: 'string?' } },
  { name: 'lock.lock', batch: 2, params: { ref: 'string?' } },
  { name: 'lock.unlock', batch: 2, params: { ref: 'string?' } },
  { name: 'area.select_box', batch: 2, params: { refs: 'string[]' } },
  { name: 'area.move_selection', batch: 2, params: { deltaX: 'number', deltaY: 'number' } },

  // --- Batch2: Resource (5) ---
  { name: 'resource.search_materials', batch: 2, params: { keyword: 'string?', pageNum: 'number?' } },
  { name: 'resource.pick_for_component', batch: 2, params: { ref: 'string', materialId: 'string', field: 'string?' } },
  { name: 'resource.collect_project_files', batch: 2 },
  { name: 'resource.upload_file', batch: 2, params: { base64: 'string', fileName: 'string' } },
  { name: 'button.render_to_image', batch: 2, params: { ref: 'string', includePress: 'boolean?' } },
  { name: 'button.upload_rendered', batch: 2, params: { ref: 'string', includePress: 'boolean?', materialName: 'string?' } },

  // --- Batch2: Validate / Engine (6) ---
  { name: 'validate.run', batch: 2 },
  { name: 'validate.locate_error', batch: 2, params: { pageId: 'string', componentId: 'string', field: 'string?' } },
  { name: 'engine.ensure_pages_loaded', batch: 2, params: { pageIds: 'string[]' } },
  { name: 'engine.flush_page', batch: 2 },
  { name: 'engine.persist_page', batch: 2 },
  { name: 'clipboard.cut', batch: 2, params: { refs: 'string[]?' } },
];

export function getToolsForLLM(batch = null) {
  const forbidden = new Set(FORBIDDEN_MUTATION_TOOLS.concat(FORBIDDEN_SAVE_TOOLS));
  const list = (batch
    ? TOOL_REGISTRY.filter((t) => t.batch <= batch)
    : TOOL_REGISTRY
  ).filter((t) => !forbidden.has(t.name));
  return list.map(({ name, description, params }) => ({
    name,
    description: description || name,
    parameters: params || {},
  }));
}

export function getToolNamesSet() {
  return new Set(TOOL_REGISTRY.map((t) => t.name));
}

export function countTools() {
  const b1 = TOOL_REGISTRY.filter((t) => t.batch === 1).length;
  const b2 = TOOL_REGISTRY.filter((t) => t.batch === 2).length;
  return { batch1: b1, batch2: b2, total: TOOL_REGISTRY.length };
}
